/**
 * 核銷頁：電子券（券碼）與套票（套票 ID）核銷
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import { packagesRedeem } from '@pos/api-sdk';
import { ApiError } from '../lib/fetcher';

const API_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL
    ? import.meta.env.VITE_API_BASE_URL
    : 'http://localhost:8787';

/**
 * 取得授權用 fetch options（與 POS 一致：staff_session_token 或 tenant_token）
 */
function getAuthFetchOptions(): RequestInit | undefined {
  if (typeof window === 'undefined') return undefined;
  const staffToken = localStorage.getItem('staff_session_token');
  const tenantToken = localStorage.getItem('tenant_token');
  const tenantId = localStorage.getItem('tenant_id');
  const token = staffToken || tenantToken;
  if (!token || !tenantId) return undefined;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Tenant-ID': tenantId,
      'Content-Type': 'application/json',
    },
  };
}

/**
 * 電子券核銷回應（成功）
 */
interface VoucherRedeemSuccess {
  success: true;
  message: string;
  timestamp: string;
}

/**
 * 電子券核銷錯誤回應（統一錯誤格式 code + message）
 */
interface VoucherRedeemError {
  code?: string;
  message?: string;
}

export function RedeemPage() {
  const [voucherCode, setVoucherCode] = useState('');
  const [packageId, setPackageId] = useState('');
  const [voucherSubmitting, setVoucherSubmitting] = useState(false);
  const [packageSubmitting, setPackageSubmitting] = useState(false);

  const authFetchOptions = getAuthFetchOptions();

  /** 電子券核銷：POST /api/vouchers/redeem */
  const handleVoucherRedeem = async () => {
    const code = voucherCode.trim();
    if (!code) {
      toast.error('請輸入券碼');
      return;
    }
    if (!authFetchOptions) {
      toast.error('請先登入');
      return;
    }
    setVoucherSubmitting(true);
    try {
      const url = `${API_BASE}/api/vouchers/redeem`;
      const res = await fetch(url, {
        ...authFetchOptions,
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      const data = (await res.json()) as
        | VoucherRedeemSuccess
        | VoucherRedeemError;
      if (res.ok && data && (data as VoucherRedeemSuccess).success) {
        toast.success((data as VoucherRedeemSuccess).message ?? '核銷成功');
        setVoucherCode('');
      } else {
        const err = data as VoucherRedeemError;
        toast.error(err.message ?? '核銷失敗');
      }
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : '核銷請求失敗';
      toast.error(msg);
    } finally {
      setVoucherSubmitting(false);
    }
  };

  /** 套票核銷：POST /api/packages/{id}/redeem */
  const handlePackageRedeem = async () => {
    const id = packageId.trim();
    if (!id) {
      toast.error('請輸入套票 ID');
      return;
    }
    if (!authFetchOptions) {
      toast.error('請先登入');
      return;
    }
    setPackageSubmitting(true);
    try {
      const result = await packagesRedeem(id, {}, authFetchOptions);
      const data = result.data as {
        success?: boolean;
        data?: { remaining_quantity?: number; order_number?: string };
        error?: string;
        message?: string;
      };
      if (result.status === 200 && data?.success) {
        const info =
          data.data?.order_number != null
            ? `訂單編號：${data.data.order_number}`
            : '';
        toast.success(`套票核銷成功${info ? `，${info}` : ''}`);
        setPackageId('');
      } else {
        const msg =
          (data as { error?: string })?.error ??
          (data as { message?: string })?.message ??
          `核銷失敗 (${result.status})`;
        toast.error(msg);
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '套票核銷請求失敗';
      toast.error(msg);
    } finally {
      setPackageSubmitting(false);
    }
  };

  const canSubmit = !!authFetchOptions;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-xl mx-auto space-y-8">
        <h1 className="text-2xl font-bold text-gray-900">核銷</h1>

        {/* 電子券核銷 */}
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            電子券核銷
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            輸入顧客出示的券碼（或掃描 QR 後貼上）進行核銷。
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={voucherCode}
              onChange={e => setVoucherCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleVoucherRedeem()}
              placeholder="請輸入券碼"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={!canSubmit}
            />
            <button
              type="button"
              onClick={handleVoucherRedeem}
              disabled={!canSubmit || voucherSubmitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {voucherSubmitting ? '核銷中…' : '核銷'}
            </button>
          </div>
        </section>

        {/* 套票核銷 */}
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">套票核銷</h2>
          <p className="text-sm text-gray-600 mb-4">
            輸入顧客出示的套票 ID（或掃描 QR 後貼上）進行核銷，將扣除一次。
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={packageId}
              onChange={e => setPackageId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePackageRedeem()}
              placeholder="請輸入套票 ID"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={!canSubmit}
            />
            <button
              type="button"
              onClick={handlePackageRedeem}
              disabled={!canSubmit || packageSubmitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {packageSubmitting ? '核銷中…' : '核銷'}
            </button>
          </div>
        </section>

        {!canSubmit && (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
            請先登入（商家或 POS 人員）後再進行核銷。
          </p>
        )}
      </div>
    </div>
  );
}
